import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import client from "@/lib/api";
import { API } from "@/lib/api";
import { mediaUrl } from "@/lib/api";
import { SectionHeader, EmptyState, notifyError, notifyOk } from "./_ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const empty = { name: "", slug: "", logo_url: "", active: true };

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => client.get("/admin/brands").then((r) => setBrands(r.data)).catch(notifyError);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...b }); setEditing(b.id); setOpen(true); };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/admin/logos", fd);
      setForm((f) => ({ ...f, logo_url: data.url }));
      notifyOk("Логотип загружен");
    } catch (e) { notifyError(e); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      if (editing) await client.put(`/admin/brands/${editing}`, form);
      else await client.post("/admin/brands", form);
      setOpen(false);
      notifyOk("Сохранено");
      load();
    } catch (e) { notifyError(e); }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить марку?")) return;
    await client.delete(`/admin/brands/${id}`);
    load();
  };

  return (
    <div>
      <SectionHeader
        title="Марки и логотипы"
        description="Управление марками и загрузка логотипов для актов."
        action={
          <Button data-testid="brand-add-btn" onClick={openNew} className="rounded-lg font-medium" style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            <Plus className="w-4 h-4 mr-1.5" /> Добавить марку
          </Button>
        }
      />

      {brands.length === 0 ? (
        <EmptyState text="Марок пока нет. Добавьте первую." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {brands.map((b) => (
            <div key={b.id} data-testid={`brand-card-${b.id}`} className="border border-neutral-200 rounded-lg bg-white p-4 flex flex-col">
              <div className="h-16 flex items-center justify-center mb-3 bg-neutral-50 rounded">
                {b.logo_url ? (
                  <img src={mediaUrl(b.logo_url)} alt={b.name} className="max-h-12 max-w-full object-contain" />
                ) : (
                  <span className="text-xs text-neutral-400">нет лого</span>
                )}
              </div>
              <div className="font-medium text-sm">{b.name}</div>
              <div className="text-xs text-neutral-400 mb-3">{b.active ? "активна" : "скрыта"}</div>
              <div className="flex gap-2 mt-auto">
                <Button size="sm" variant="outline" data-testid={`brand-edit-${b.id}`} onClick={() => openEdit(b)} className="flex-1">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" data-testid={`brand-delete-${b.id}`} onClick={() => remove(b.id)} className="text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Редактировать марку" : "Новая марка"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Название</Label>
              <Input data-testid="brand-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Логотип (PNG/SVG)</Label>
              <input data-testid="brand-logo-file" type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files[0])} className="text-sm" />
              {uploading && <p className="text-xs text-neutral-500">Загрузка…</p>}
              {form.logo_url && <img src={mediaUrl(form.logo_url)} alt="" className="max-h-12 mt-2 object-contain" />}
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="brand-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Активна</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Отмена</Button>
            <Button data-testid="brand-save-btn" onClick={save} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
